import * as React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import loadable from '@loadable/component';
import { RouteComponentProps, navigate, Link } from '@reach/router';
import {
  ProductVariantFacet,
  ProductViewModel,
  ProductState,
  TransactionalCurrency,
  ProductFeature,
  ProductMediaItemViewModel,
  ProductMediaType,
  Website,
  BasketRowViewModel,
  OfferTypes,
  Ecommerce
} from '../types/generated';
import canUseDOM from '../utilities/canUseDOM';
import { createCurrencyCode } from '../utilities/formatCurrency';
import StockCtas from './StockCtas';
import { ProductPageProps } from '../apps/product/ProductPage';
import { Variants } from '../apps/product/components/Variants';
import Roundels from './Roundels';
import Icon from './Icon';
import Alert from './Alert';
import { JsonView } from './JsonView';
import updateQuantityBadge from '../utilities/updateQuantityBadge';
import { addToBasket, getBasketCatalogueRefs } from '../apps/basket/basketApi';
import { getAverageStarRating } from '../apps/trustpilot/trustPilotApi';
import popupAlert from '../utilities/popup-alert';
import LoaderMask from './LoaderMask';
import AddToSaveForLater from '../apps/save-for-later/AddToSaveForLater';
import TrustBox from '../apps/trustpilot/TrustBox';
import NavLinks from '../apps/product/components/NavLinks';
import HtmlText from './HtmlText';
import Accordion from './Accordion';
import AverageStarRating from '../apps/trustpilot/AverageStarRating';
import Loader from './Loader';
import * as basketApi from '../apps/basket/basketApi';
import Offer from '../apps/Offer/Offer';
import OfferPrompt from '../apps/Offer/OfferPrompt';
import {
  getProductByProductId,
  getProductQuantityInBasket
} from '../apps/product/productPageApi';
import ProductLessons from '../apps/product/components/ProductLessons';
import { addGA4ProductEvent } from '../helpers/ga4';

// Dynamic imports - default
const ProductMediaGallery = loadable(
  () =>
    import(
      /* webpackPreload: true, webpackChunkName: "productMediaGallery" */ './ProductMediaGallery'
    )
);

const AddToAny = loadable(
  () =>
    import(
      /* webpackChunkName: "addToAny" */ '../apps/product/components/AddToAny'
    )
);

const AddToFavourites = loadable(
  () =>
    import(
      /* webpackChunkName: "addToFavourites" */ '../apps/product/components/AddToFavourites'
    )
);

const AddToWishlist = loadable(
  () =>
    import(
      /* webpackChunkName: "addToWishlist" */ '../apps/wishlist/AddToWishlist'
    )
);

const AddToMyList = loadable(
  () =>
    import(/* webpackChunkName: "addToMylist" */ '../apps/wishlist/AddToMyList')
);

const Download = loadable(
  () =>
    import(
      /* webpackChunkName: "download" */ '../apps/product/components/Download'
    )
);

const AlternativeProductPod = loadable(
  () =>
    import(
      /* webpackChunkName: "alternativeProductPod" */ './AlternativeProductPod'
    )
);

// Dynamic imports - named
const ProductPrices = loadable(async () => {
  const { ProductPrices } = await import(
    /* webpackChunkName: "productPrices" */ '../apps/product/components/ProductPrices'
  );
  return ProductPrices;
});

const PriceBreaks = loadable(
  () =>
    import(
      /* webpackChunkName: "alternativeProductPod" */ '../apps/product/components/PriceBreaks'
    )
);

type TabTypes = 'description' | 'reviews';

export interface NumberOfReviews {
  OneStar?: number | null;
  TwoStars?: number | null;
  ThreeStars?: number | null;
  FourStars?: number | null;
  FiveStars?: number | null;
  Total?: number | null;
}

interface ProductProps extends ProductPageProps, RouteComponentProps {
  quantity?: number;
  catalogueRefParam?: string;
  baseUrl: string;
  showProductReviews: boolean;
  showDocuments: boolean;
  showUsefulLinks: boolean;
  trustPilotBusinessUnitId: string | null;
  website: Website;
  isCssFavouritesEnabled: boolean;
}

const Product = ({
  quantity: initialQuantity,
  isLoggedIn,
  product: initialProduct,
  transactionalCurrency,
  catalogueRefParam = '',
  baseUrl,
  showSecondaryPrice,
  showProductReviews,
  showDocuments,
  showUsefulLinks,
  trustPilotBusinessUnitId,
  website,
  isCssFavouritesEnabled,
  ...props
}: ProductProps) => {
  const [minQuantity, setMinQuantity] = useState<number>(
    initialProduct &&
      !initialProduct.isVariant &&
      initialProduct.isUnitPriceDisabled
      ? initialProduct.priceBreakMinUnits
      : initialQuantity ?? 1
  );

  const [quantity, setQuantity] = useState<number>(minQuantity || 1);

  const [product, setProduct] = useState(initialProduct);
  const [showMoreOptions, setShowMoreOptions] = useState(props.showMoreOptions);
  const [showFeatures, setShowFeatures] = useState(props.showFeatures);

  const [isLoading, setIsLoading] = useState(false);
  const [isJustAdded, setIsJustAdded] = useState(false);
  const [basketQty, setBasketQty] = useState<number>(0);
  const [offerFreeProduct, setOfferFreeProduct] =
    useState<ProductViewModel | null>(null);
  // used to determine if page has already loaded
  const loadCount = useRef(0);

  const [selectedTab, setSelectedTab] = useState<TabTypes>('description');

  // Trustpilot
  const [averageStarRating, setAverageStarRating] =
    useState<number | null>(null);
  const [hasPositiveNumberReviews, setHasPositiveNumberReviews] =
    useState<boolean>(false);
  const [numberOfReviews, setNumberOfReviews] = useState<NumberOfReviews>({
    OneStar: null,
    TwoStars: null,
    ThreeStars: null,
    FourStars: null,
    FiveStars: null,
    Total: null
  });

  const hasReviews = showProductReviews && hasPositiveNumberReviews;
  const hasSecondaryContent = hasReviews;

  function handleQuantityChange(quantity: number) {
    setQuantity(quantity);
  }

  function handleQuantityOnBlur(quantity: number) {
    if (
      typeof quantity === 'number' &&
      typeof minQuantity === 'number' &&
      quantity < minQuantity
    ) {
      quantity = minQuantity;
      if (product?.isUnitPriceDisabled) {
        popupAlert.showNegative(
          `This product has a minimum order quantity of ${product?.priceBreakMinUnits}.`
        );
      }
    }
    setQuantity(quantity);
  }

  function handleAddToBasket(e: React.FormEvent) {
    e.preventDefault();
    if (!product || !product.catalogueRef) return;

    const qty =
      typeof quantity === 'string'
        ? typeof minQuantity === 'number'
          ? minQuantity
          : 1
        : quantity;
    addItemToBasket(product.productId, product.catalogueRef, qty).catch(
      (error: Error) => {
        console.error(error);
      }
    );
  }

  function handleToggleFavourites(isAdded: boolean) {
    if (isAdded) addGA4ProductEvent('add_to_favourites', product!, {});
    else addGA4ProductEvent('remove_from_favourites', product!, {});
  }

  function handleAddedToWishlist() {
    addGA4ProductEvent('add_to_wishlist', product!, {});
  }

  function addItemToBasket(
    productId: number,
    catalogueRef: string,
    qty: number
  ) {
    addGA4ProductEvent(
      'add_to_cart',
      product as ProductViewModel,
      {
        item_list_id: 'pdp',
        item_list_name: 'PDP'
      } as Ecommerce,
      qty
    );

    return addToBasket(productId, qty, catalogueRef, false).then(
      (response: any) => {
        if (response.success && response.warning) {
          popupAlert.showWarning(response.message);
        } else if (!response.success) {
          popupAlert.showNegative(response.message);
        } else {
          popupAlert.showPositive(response.message);
          setIsJustAdded(true);
        }

        trackAddToBasket();
        trackSqueezelyAddToBasket();
        updateQuantityBadge(response.basketItemCount);
        getBasketQuantity().then((result: number) => {
          setBasketQty(result);
        });
      }
    );
  }

  const getBasketQuantity = useCallback(async () => {
    let result = 0;
    let catalogueRef = product?.catalogueRef;

    if (catalogueRef) {
      await getProductQuantityInBasket(catalogueRef).then((data: number) => {
        result = data;
      });
    }

    return result;
  }, [product]);

  useEffect(() => {
    const item = product;
    if (item && item.offerFreeProductID && item.offerFreeProductID > 0) {
      getProductByProductId(product?.offerFreeProductID ?? 0).then(
        (data: ProductViewModel) => {
          setOfferFreeProduct(data);
        }
      );
    }

    addGA4ProductEvent(
      'view_item',
      item as ProductViewModel,
      {
        item_list_id: 'pdp',
        item_list_name: 'PDP'
      } as Ecommerce
    );
  }, [product]);

  useEffect(() => {
    if (
      product &&
      product.catalogueRef &&
      (product.offerTypeName === OfferTypes.ExtraFree ||
        product?.offerTypeName === OfferTypes.FreeProduct)
    ) {
      getBasketQuantity().then((result: number) => {
        setBasketQty(result);
      });
    }
  }, [getBasketQuantity, product]);
  /**
   * Variants
   */

  const variants = product && product.variants;
  const facetGroups = variants && variants.facets;

  function getCurrentSelectionIds(facetGroups: ProductVariantFacet[]) {
    const selection: number[] = [];

    if (!facetGroups) return selection;

    facetGroups.forEach((facetGroup) => {
      if (facetGroup && facetGroup.facetValues) {
        facetGroup.facetValues.forEach((facet) => {
          if (facet.isSelected) {
            selection.push(facet.facetValueId);
          }
        });
      }
    });

    return selection;
  }

  function trackSqueezelyAddToBasket() {
    window._sqzl = window._sqzl || [];
    window._sqzl.push({
      event: 'AddToCart',
      products: [
        {
          id: product?.catalogueRef,
          quantity: quantity
        }
      ]
    });

    console.log('Completed adding product AddToCart event sent to Squeezely');
  }

  function trackAddToBasket() {
    window.monetateQ = window.monetateQ || [];
    window.monetateQ.push(['setPageType', 'product']);
    window.monetateQ.push([
      'addProductDetails',
      [
        {
          productId: `${product?.catalogueRef}`,
          sku: `${product?.sku}`,
          quantity: quantity,
          currency: createCurrencyCode(transactionalCurrency)
        }
      ]
    ]);
    basketApi
      .getBasket()
      .then((response) => {
        if (response.hasRows) {
          response.rows.forEach((element: BasketRowViewModel) => {
            window.monetateQ.push([
              'addCartRows',
              [
                {
                  productId: element.catalogueRef,
                  sku: element.sku,
                  quantity: element.quantity,
                  unitPrice: element.priceExcVat,
                  currency: createCurrencyCode(transactionalCurrency)
                }
              ]
            ]);
          });
        }
        window.monetateQ.push(['trackData']);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  function handleVariantSelection(
    facetGroupId: number,
    facetId: number | null
  ) {
    if (facetGroups) {
      const newFacetGroups = facetGroups.map((facetGroup) => {
        if (facetGroup.id !== facetGroupId) {
          return facetGroup;
        }

        const fGroup: ProductVariantFacet = {
          ...facetGroup,
          facetValues:
            facetGroup.facetValues &&
            facetGroup.facetValues.map((facet) => ({
              ...facet,
              isSelected: facet.facetValueId === facetId
            }))
        };

        return fGroup;
      });

      if (product && variants) {
        const updatedProduct: ProductViewModel = {
          ...product,
          variants: {
            ...variants,
            facets: newFacetGroups
          }
        };

        setProduct(updatedProduct);

        const params = getCurrentSelectionIds(newFacetGroups).join('-');
        console.log('navigating to variant');
        console.log('baseUrl', baseUrl);
        navigate(
          `${baseUrl}/${catalogueRefParam.toLowerCase()}${
            params ? `?f=${params}` : ``
          }`
        );
      }
    }
  }

  useEffect(() => {
    if (document.activeElement?.getAttribute('name') === 'quantity') return;
    const minQty =
      product?.isUnitPriceDisabled &&
      !product.isVariant &&
      product.priceBreakMinUnits > 1
        ? product.priceBreakMinUnits
        : 1;
    if (
      product?.isUnitPriceDisabled &&
      !product.isVariant &&
      typeof quantity === 'number' &&
      quantity < minQty
    ) {
      setQuantity(minQty);
    }

    setMinQuantity(minQty);
  }, [product, quantity]);
  /**
   * Get Data
   */

  const qsParams = props.location?.search || '';

  useEffect(() => {
    // https://stackoverflow.com/questions/53253940/make-react-useeffect-hook-not-run-on-initial-render
    if (loadCount.current < 1) {
      // this counts the number of times the component has re-rendered and if
      // it's less that 1 times it should not get data
      // using a ref to prevent re-renders
      // Api calls are made to product info and basket ref endpoints
      loadCount.current = loadCount.current + 1;
      return;
    }

    console.log('=== getting data ===');
    setIsLoading(true);

    const catalogueRef = catalogueRefParam.toLowerCase();

    console.log(
      `/umbraco/surface/ProductsSurface/GetVariants?c=${catalogueRef}${qsParams.replace(
        '?',
        '&'
      )}`
    );

    if (canUseDOM() && catalogueRef) {
      fetch(
        `/umbraco/surface/ProductsSurface/GetVariants?c=${catalogueRef}${qsParams.replace(
          '?',
          '&'
        )}`
      )
        .then((res) => res.json())
        .then((data: ProductState) => {
          // console.log('my data', data.product);
          if (data && data.product) {
            setProduct(data.product);
            setShowMoreOptions(data.showMoreOptions);
            setShowFeatures(data.showFeatures);
          } else {
            popupAlert.showNegative(
              'There was a problem loading the product information, please try refreshing the page'
            );
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error(err);
          popupAlert.showNegative(
            'There was a problem loading the product information, please try refreshing the page'
          );
          setIsLoading(false);
        });
    }
  }, [catalogueRefParam, qsParams]);

  const [catalogueRefs, setCatalogueRefs] = useState<string[]>([]);

  useEffect(() => {
    if (canUseDOM()) {
      getBasketCatalogueRefs().then((refs) => {
        setCatalogueRefs(refs);
      });
    }
  }, []);

  function renderUnitOfSale() {
    if (
      product &&
      product.unitOfSale &&
      product.unitOfSale !== 'Each' &&
      product.unitOfSale !== 'Pack'
    ) {
      return (
        <span className="product-details__uos"> {product.unitOfSale}</span>
      );
    }
    return null;
  }

  function renderVariants() {
    if (
      !product ||
      !product.variants
      // note: removed this because otherwise the 'more options'
      // never shows
      // ||
      // !product.variants.facets ||
      // product.variants.facets.length < 1
    ) {
      return null;
    }

    // if (!product.isVariant && product.variantHeader && !product.variants.variantCatalogRef) {
    if (showMoreOptions && product.variantHeader && !product.isRestricted) {
      return (
        <div className="mb-3">
          <Link
            to={`${baseUrl}/${product.variantHeader.toLowerCase()}`}
            className="btn btn-outline-primary btn-sm"
          >
            More Options
          </Link>
        </div>
      );
    }

    if (product.variantHeader || product.isVariant) {
      return (
        <Variants
          onChange={handleVariantSelection}
          variants={product.variants}
        />
      );
    }
  }

  if (!product) {
    return (
      <Alert type="danger">
        There was a problem loading the product. Please try refreshing the page.
      </Alert>
    );
  }

  const fallbackImage =
    'https://cdn.images.fecom-media.com/image_coming_soon.jpg';

  const prices = () => (
    <ProductPrices
      fallback={<Loader size="xsmall" />}
      product={product}
      transactionalCurrency={
        transactionalCurrency || TransactionalCurrency.None
      }
      isAdded={
        product.catalogueRef != null
          ? catalogueRefs.includes(product.catalogueRef)
          : false
      }
      showSecondaryPrice={showSecondaryPrice}
      priceBreak={product.priceBreaks
        ?.filter(
          (pricebreak) =>
            pricebreak.units <= (typeof quantity === 'number' ? quantity : 1)
        )
        .slice(-1)
        .pop()}
      website={website}
    />
  );

  const renderCtasAndQuantity = (ariaId: string = '') => (
    <>
      <div className="product-details__atb">
        <div className="add-to-basket add-to-basket--qty">
          {product.isVariant && (
            <button
              className="btn btn-primary btn--select-options"
              disabled={true}
            >
              Select Options
            </button>
          )}
          {!product.isVariant &&
            !product.isRestricted &&
            !product.isTemporarilyUnavailable && (
              <form
                className="d-lg-flex justify-content-lg-center align-items-center"
                onSubmit={handleAddToBasket}
              >
                <label
                  htmlFor={`quantity${ariaId}`}
                  className="add-to-basket__label"
                >
                  Quantity:{' '}
                </label>
                <div className="add-to-basket__plus-minus-input">
                  <div
                    className="icons"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (minQuantity && quantity - 1 < minQuantity) {
                        return;
                      }
                      setQuantity(quantity - 1);
                    }}
                  >
                    -
                  </div>
                  <input
                    id={`quantity${ariaId}`}
                    className="value"
                    name="quantity"
                    onBlur={(e) => {
                      const quantity = parseInt(e.target.value, 10);
                      handleQuantityOnBlur(quantity || minQuantity);
                    }}
                    onChange={(e) => {
                      const quantity = parseInt(e.target.value, 10);
                      handleQuantityChange(quantity || minQuantity);
                    }}
                    value={quantity || ''}
                    //min={minQuantity}
                    type="number"
                    data-hj-whitelist
                  />
                  <div
                    className="icons"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setQuantity(quantity + 1);
                    }}
                  >
                    +
                  </div>
                </div>
                <span className="add-to-basket__button">
                  <button
                    type="submit"
                    className="btn btn-primary btn--alt-colour"
                  >
                    {product.isAdded || isJustAdded
                      ? 'Added to Basket'
                      : 'Add to Basket'}
                  </button>
                </span>
              </form>
            )}
        </div>
        {!product.isVariant &&
          (product.isRestricted || product.isTemporarilyUnavailable) && (
            <button
              disabled
              className="btn btn-block btn-primary btn--is-restricted"
            >
              {product.isRestricted
                ? 'Product is restricted'
                : 'Temporarily Unavailable'}
            </button>
          )}
      </div>
      {product.stockInfo?.isStockRelevant &&
        (product.stockInfo?.isOutOfStock ||
          product.stockInfo?.isDelayedStock) && (
          <StockCtas
            isLoggedIn={isLoggedIn}
            hasOutOfStockAlternativeProductUrl={
              product.hasOutOfStockAlternativeProductUrl
            }
            outOfStockAlternativeProductUrl={
              product.outOfStockAlternativeProductUrl
            }
            catalogueRef={product.catalogueRef}
            isOutOfStock={product.stockInfo?.isOutOfStock}
            isDelayedStock={product.stockInfo?.isDelayedStock}
            isBackInStockNotifyEnabled={
              product.stockInfo?.isBackInStockNotifyEnabled
            }
          />
        )}
    </>
  );

  /* eslint-disable react-hooks/rules-of-hooks */
  // Trustpilot
  useEffect(() => {
    if (showProductReviews) {
      getAverageStarRating(product.catalogueRef)
        .then((data: any) => {
          setAverageStarRating(data?.StarsAverage);
          setNumberOfReviews(data?.NumberOfReviews);
          setHasPositiveNumberReviews(data?.NumberOfReviews.Total > 0);
        })
        .catch((err: any) => console.error(err));
    }
  }, [showProductReviews, product.catalogueRef]);

  // keep in this component for #hash links to scroll page
  function renderDescriptionTabsMobile() {
    if (product) {
      return (
        <div className="d-block d-lg-none">
          <div className="product-description" role="tablist">
            <h2
              id="description"
              className="product-description__title"
              role="tab"
              aria-controls="descriptionPanel1"
              aria-selected={selectedTab === 'description' ? 'true' : 'false'}
            >
              <a
                className={`product-tab ${
                  selectedTab === 'description' ? 'product-tab--selected' : ''
                }`}
                id="descriptionTab1"
                href="#descriptionTab1"
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedTab('description');
                }}
              >
                Product Description
              </a>
            </h2>
            {product.description && selectedTab === 'description' && (
              <div
                id="descriptionPanel1"
                aria-labelledby="descriptionTab1"
                role="tabpanel"
                className="product-description__content"
              >
                <HtmlText component="div">{product.description}</HtmlText>

                <div className="product-description__accordion">
                  {product.reasonsToLove && (
                    <Accordion
                      id="reasonsToLoveLink1"
                      title="Reasons to Love"
                      ariaControls="reasonsToLovePanel1"
                      ariaLabelledBy="reasonsToLoveLink1"
                      expanded={true}
                    >
                      <HtmlText component="div">
                        {product.reasonsToLove}
                      </HtmlText>
                    </Accordion>
                  )}

                  {product.learningOutcomes && (
                    <Accordion
                      id="learningOutcomesLink1"
                      title="Learning Outcomes"
                      ariaControls="learningOutcomesPanel1"
                      ariaLabelledBy="learningOutcomesLink1"
                      expanded={true}
                    >
                      <HtmlText component="div">
                        {product.learningOutcomes}
                      </HtmlText>
                    </Accordion>
                  )}

                  {product.topTips && (
                    <Accordion
                      id="topTipsLink1"
                      title="Top Tips"
                      ariaControls="topTipsPanel1"
                      ariaLabelledBy="topTipsLink1"
                      expanded={true}
                    >
                      <HtmlText component="div">{product.topTips}</HtmlText>
                    </Accordion>
                  )}
                </div>
              </div>
            )}

            {hasSecondaryContent && (
              <>
                {hasReviews && (
                  <>
                    <h2
                      className="product-description__title"
                      role="tab"
                      aria-controls="reviewsPanel1"
                      aria-selected={
                        selectedTab === 'reviews' ? 'true' : 'false'
                      }
                    >
                      <a
                        className={`product-tab ${
                          selectedTab === 'reviews'
                            ? 'product-tab--selected'
                            : ''
                        }`}
                        id="reviewsTab1"
                        href="#reviewsTab1"
                        onClick={(e) => {
                          e.preventDefault();
                          setSelectedTab('reviews');
                        }}
                      >
                        Reviews
                      </a>
                    </h2>

                    {selectedTab === 'reviews' && (
                      <div
                        id="reviewsPanel1"
                        aria-labelledby="reviewsTab1"
                        role="tabpanel"
                        className="product-description__content"
                      >
                        <TrustBox
                          businessUnitId={trustPilotBusinessUnitId}
                          trustBoxTemplateId="544a426205dc0a09088833c6"
                          trustBoxHeight="500px"
                          productSku={product.sku}
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
          {product.productLessons?.hasProductLessons && (
            <div className="product-details__product-lessons mt-1 mb-4">
              <ProductLessons productLessons={product.productLessons} />
            </div>
          )}
          {showFeatures && (
            <section className="product-details__further-information mt-1 mb-4">
              <h3 className="section-heading section-heading--sm pt-2">
                Further Information
              </h3>
              <dl className="description-list container">
                {product.features &&
                  product.features.length > 0 &&
                  product.features.map((feature: ProductFeature) => {
                    const featureValues: string[] | undefined =
                      feature.value?.split(',');
                    return (
                      <div
                        key={`${feature.name}`}
                        className="description-list__values row row-striped row-align-center p-1"
                      >
                        <dt className="description-title col-12 col-sm-4 col-md-6">
                          {feature.name}
                        </dt>
                        <dd className="description-detail col-12 col-sm-8 col-md-6 mb-0">
                          {featureValues &&
                            featureValues.map(
                              (value: string, index: number) => (
                                <div key={`${index}`}>
                                  {value}
                                  {featureValues?.length !== index + 1 && (
                                    <br />
                                  )}
                                </div>
                              )
                            )}
                        </dd>
                      </div>
                    );
                  })}
              </dl>
            </section>
          )}

          {showDocuments && (
            <section className="product-details__documents py-2 mb-4">
              <h3 className="section-heading section-heading--sm pt-2">
                Documents
              </h3>
              <ul className="list-unstyled pl-3 mb-0">
                {product?.documents?.map(
                  (document: ProductMediaItemViewModel) => (
                    <li key={document.productMediaID} className="py-1">
                      <a
                        href={`${document.value}`}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {renderFileTypeIcon(document.mediaType)}{' '}
                        {document.title}
                      </a>
                    </li>
                  )
                )}
              </ul>
            </section>
          )}

          {showUsefulLinks && (
            <section className="product-details__useful-links mb-4">
              <h3 className="section-heading section-heading--sm pt-2">
                Useful Links
              </h3>
              <ul className="useful-links list-unstyled pl-3">
                {product?.links?.map((link: ProductMediaItemViewModel) => (
                  <li key={link.productMediaID} className="py-1">
                    <a
                      className="d-block"
                      href={`${link.value}`}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      {link.title}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="product-details__side">
            {transactionalCurrency !== null && (
              <div className="product-details__alternative">
                <AlternativeProductPod
                  fallback={<Loader size="xsmall" />}
                  alternativeProducts={product.alternativeProducts}
                  transactionalCurrency={transactionalCurrency}
                />
              </div>
            )}
          </div>
        </div>
      );
    }
  }

  // keep in this component for #hash links to scroll page
  function renderDescriptionTabsDesktop() {
    if (product) {
      return (
        <div className="d-none d-lg-block">
          <div className="product-description">
            <div className="product-description__tabs" role="tablist">
              <h2
                id="description"
                className="product-description__title"
                role="tab"
                aria-controls="descriptionPanel"
                aria-selected={selectedTab === 'description' ? 'true' : 'false'}
              >
                <a
                  className={`product-tab ${
                    selectedTab === 'description' ? 'product-tab--selected' : ''
                  }`}
                  id="descriptionTab"
                  href="#descriptionTab"
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedTab('description');
                  }}
                >
                  Product Description
                </a>
              </h2>

              {hasSecondaryContent && (
                <>
                  {hasReviews && (
                    <>
                      <span className="mx-2 product-tab__divider">|</span>
                      <h2
                        className="product-description__title"
                        role="tab"
                        aria-controls="reviewsPanel"
                        aria-selected={
                          selectedTab === 'reviews' ? 'true' : 'false'
                        }
                      >
                        <a
                          className={`product-tab ${
                            selectedTab === 'reviews'
                              ? 'product-tab--selected'
                              : ''
                          }`}
                          id="reviewsTab"
                          href="#reviewsTab"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedTab('reviews');
                          }}
                        >
                          Reviews
                        </a>
                      </h2>
                    </>
                  )}
                </>
              )}
            </div>

            {product.description && selectedTab === 'description' && (
              <div
                id="descriptionPanel"
                aria-labelledby="descriptionTab"
                role="tabpanel"
                className="product-description__content"
              >
                <HtmlText component="div">{product.description}</HtmlText>

                <div className="product-description__accordion">
                  {product.reasonsToLove && (
                    <Accordion
                      id="reasonsToLoveLink"
                      title="Reasons to Love"
                      ariaControls="reasonsToLovePanel"
                      ariaLabelledBy="reasonsToLoveLink"
                      expanded={true}
                    >
                      <HtmlText component="div">
                        {product.reasonsToLove}
                      </HtmlText>
                    </Accordion>
                  )}

                  {product.learningOutcomes && (
                    <Accordion
                      id="learningOutcomesLink"
                      title="Learning Outcomes"
                      ariaControls="learningOutcomesPanel"
                      ariaLabelledBy="learningOutcomesLink"
                      expanded={true}
                    >
                      <HtmlText component="div">
                        {product.learningOutcomes}
                      </HtmlText>
                    </Accordion>
                  )}

                  {product.topTips && (
                    <Accordion
                      id="topTipsLink"
                      title="Top Tips"
                      ariaControls="topTipsPanel"
                      ariaLabelledBy="topTipsLink"
                      expanded={true}
                    >
                      <HtmlText component="div">{product.topTips}</HtmlText>
                    </Accordion>
                  )}
                </div>
              </div>
            )}
            {/* end description panel */}

            {selectedTab === 'reviews' && (
              <div
                id="reviewsPanel"
                aria-labelledby="reviewsTab"
                role="tabpanel"
                className="product-description__content"
              >
                <TrustBox
                  businessUnitId={trustPilotBusinessUnitId}
                  trustBoxTemplateId="544a426205dc0a09088833c6"
                  trustBoxHeight="500px"
                  productSku={product.sku}
                />
              </div>
            )}
          </div>
        </div>
      );
    }
  }

  function renderFileTypeIcon(mediaType: ProductMediaType) {
    let iconName;
    switch (mediaType) {
      case ProductMediaType.pdf:
        iconName = 'file-pdf-o';
        break;
      case ProductMediaType.doc:
      case ProductMediaType.docx:
      case ProductMediaType.odt:
      case ProductMediaType.txt:
        iconName = 'file-word-o';
        break;
      case ProductMediaType.xls:
      case ProductMediaType.xslx:
      case ProductMediaType.ods:
        iconName = 'file-excel-o';
        break;
      case ProductMediaType.ppt:
      case ProductMediaType.pptx:
      case ProductMediaType.odp:
        iconName = 'file-powerpoint-o';
        break;
      default:
        return;
    }
    return <Icon iconName={iconName} modifierCss="icon--file" />;
  }

  function renderPriceBreaks() {
    if (product) {
      const priceBreaks =
        product.isUnitPriceDisabled && product.priceBreaks
          ? product.priceBreaks.slice(1)
          : product.priceBreaks;

      if (priceBreaks && priceBreaks.length > 0) {
        return (
          <div className="product-details__price-breaks">
            <PriceBreaks
              fallback={<Loader size="xsmall" />}
              priceBreaks={priceBreaks}
              transactionalCurrency={transactionalCurrency}
              website={website}
              showSecondaryPrice={showSecondaryPrice}
              updateQuantity={handleQuantityChange}
            />
          </div>
        );
      }
    }
  }

  return (
    <main role="main" className="product">
      {isLoading && <LoaderMask title="Loading..." type="absolute" />}
      <h1 className="product-title">
        {product.name} {renderUnitOfSale()}
      </h1>

      {/* Mobile */}
      {hasReviews && (
        <div className="d-block d-md-none mb-3">
          <AverageStarRating
            businessUnitId={trustPilotBusinessUnitId}
            averageStarRating={averageStarRating}
            numberOfReviews={numberOfReviews}
            hasPositiveNumberReviews={hasPositiveNumberReviews}
          />
        </div>
      )}

      <div className="row">
        <div className="col-md-5 col-lg-6">
          <div className="product-gallery">
            <div className="roundel-wrapper">
              <Roundels roundel={product.roundel} />
              <Roundels roundel={product.secondaryRoundel} />
            </div>

            {isLoading && <div style={{ paddingTop: '100%' }} />}
            {!isLoading && (
              <>
                {/* Mobile */}
                <div className="d-block d-lg-none">
                  <ProductMediaGallery
                    primaryImageUrl={product.imageUrl || fallbackImage}
                    images={product.images || []}
                    videos={product.videos || []}
                    thumbnailPosition="bottom"
                    catRef={product.catalogueRef ?? ''}
                    isCssFavouritesEnabled={isCssFavouritesEnabled}
                    isVariant={product.isVariant}
                  />
                </div>
                {/* Desktop */}
                <div className="d-none d-lg-block">
                  <ProductMediaGallery
                    primaryImageUrl={product.imageUrl || fallbackImage}
                    images={product.images || []}
                    videos={product.videos || []}
                    thumbnailPosition="left"
                    catRef={product.catalogueRef ?? ''}
                    isCssFavouritesEnabled={isCssFavouritesEnabled}
                    isVariant={product.isVariant}
                  />
                </div>
              </>
            )}
          </div>
          {product.isDiscountExcluded && (
            <div
              className="product-details__exempt offset-lg-2 text-muted my-3"
              data-testing="item-exempt"
            >
              <small>
                This product is not eligible for any further discount
              </small>
            </div>
          )}
        </div>
        <div className="col-md-7 col-lg-6">
          <div className="product__details">
            <div className="product-details">
              <div className="product-details__top">
                <div className="product-details__intro">
                  <h1 className="product-details__title">
                    {product.name} {renderUnitOfSale()}
                  </h1>

                  {!product.isVariant && (
                    <>
                      <div className="d-block d-lg-none">
                        <div className="product-details__code">
                          {product.catalogueRef}
                        </div>
                      </div>
                      <div className="d-none d-lg-block">
                        <div className="product-details__code">
                          <p>Product code: {product.catalogueRef}</p>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Tablet/Desktop */}
                  {hasReviews && (
                    <div className="d-none d-md-block my-2">
                      <AverageStarRating
                        businessUnitId={trustPilotBusinessUnitId}
                        averageStarRating={averageStarRating}
                        numberOfReviews={numberOfReviews}
                        hasPositiveNumberReviews={hasPositiveNumberReviews}
                      />
                    </div>
                  )}

                  <div className="d-none d-lg-block product-details__nav">
                    <NavLinks
                      hasProductLessons={
                        !!product.productLessons?.hasProductLessons
                      }
                      reasonsToLove={!!product.reasonsToLove}
                      learningOutcomes={!!product.learningOutcomes}
                      topTips={!!product.topTips}
                    />
                  </div>

                  {/* Mobile/Tablet */}
                  <div className="d-block d-lg-none mb-4">
                    {product.directDelivery && (
                      <div className="product-details__delivery-notice">
                        <Icon
                          iconName="truck-new"
                          modifierCss="delivery-notice-icon"
                          size={2}
                        />
                        <span className="delivery-notice-text">
                          {product.directDeliveryText}
                        </span>
                      </div>
                    )}
                    {renderVariants()}
                    {prices()}

                    <div className="d-block d-lg-none">
                      <Offer
                        offer={{
                          productCatalogueRef: product.catalogueRef,
                          offerDescription: product.offerDescription,
                          offerFreeProductId: product.offerFreeProductID,
                          offerName: product.offerName,
                          offerQuantityBuy: product.offerQuantityBuy,
                          offerQuantityFree: product.offerQuantityFree,
                          offerTypeId: product.offerTypeID,
                          offerTypeName: product.offerTypeName
                        }}
                        className="offer-details-link"
                        offerFreeProduct={offerFreeProduct}
                      />
                    </div>

                    <div className="d-block d-lg-none">
                      <OfferPrompt
                        basketQty={basketQty}
                        offer={{
                          productCatalogueRef: product.catalogueRef,
                          offerDescription: product.offerDescription,
                          offerFreeProductId: product.offerFreeProductID,
                          offerName: product.offerName,
                          offerQuantityBuy: product.offerQuantityBuy,
                          offerQuantityFree: product.offerQuantityFree,
                          offerTypeId: product.offerTypeID,
                          offerTypeName: product.offerTypeName
                        }}
                        handleQuantityChange={(
                          quantity: number,
                          catalogueRef: string
                        ) => {
                          if (!product || !product.catalogueRef) return;
                          addItemToBasket(0, catalogueRef, quantity);
                        }}
                        className="offer-prompt"
                        offerFreeProduct={offerFreeProduct}
                      />
                    </div>

                    {renderCtasAndQuantity('1')}
                    <div className="product-details__ctas">
                      {!product.isRestricted && !product.isVariant && (
                        <>
                          {isCssFavouritesEnabled ? (
                            <div
                              className={`my-list ${
                                !isLoggedIn || false ? 'ml-0 w-25' : ''
                              }`}
                            >
                              <AddToMyList
                                catalogueRef={product.catalogueRef}
                                isLoggedIn={isLoggedIn}
                                quantity={
                                  typeof quantity === 'number' ? quantity : 1
                                }
                              />
                            </div>
                          ) : (
                            <>
                              <AddToFavourites
                                fallback={<Loader size="xsmall" />}
                                catalogueRef={product.catalogueRef}
                                isLoggedIn={isLoggedIn}
                                handleToggleFavourites={handleToggleFavourites}
                              />
                              {isLoggedIn ? (
                                <AddToWishlist
                                  catalogueRef={product.catalogueRef}
                                  size="sm"
                                  isBasketPage={false}
                                  handleAddedToWishlist={handleAddedToWishlist}
                                />
                              ) : (
                                <AddToSaveForLater
                                  quantity={quantity}
                                  catalogueRef={product.catalogueRef}
                                  modifier={product.modifier}
                                  size="sm"
                                />
                              )}
                            </>
                          )}
                          {/* todo flag isIntl */}
                          <Download catalogueRef={product.catalogueRef} />
                        </>
                      )}
                    </div>
                    {product.hasPriceBreak && renderPriceBreaks()}
                    {renderDescriptionTabsMobile()}
                  </div>
                  {/* end Mobile/Tablet */}

                  {/* Desktop */}
                  <div className="d-none d-lg-flex justify-content-between">
                    {prices()}
                    {renderVariants()}
                  </div>

                  <div className="d-none d-lg-flex justify-content-between">
                    <Offer
                      offer={{
                        productCatalogueRef: product.catalogueRef,
                        offerDescription: product.offerDescription,
                        offerFreeProductId: product.offerFreeProductID,
                        offerName: product.offerName,
                        offerQuantityBuy: product.offerQuantityBuy,
                        offerQuantityFree: product.offerQuantityFree,
                        offerTypeId: product.offerTypeID,
                        offerTypeName: product.offerTypeName
                      }}
                      className="offer-details-link"
                      offerFreeProduct={offerFreeProduct}
                    />
                  </div>

                  <div className="d-none d-lg-flex justify-content-between">
                    <OfferPrompt
                      basketQty={basketQty}
                      offer={{
                        productCatalogueRef: product.catalogueRef,
                        offerDescription: product.offerDescription,
                        offerFreeProductId: product.offerFreeProductID,
                        offerName: product.offerName,
                        offerQuantityBuy: product.offerQuantityBuy,
                        offerQuantityFree: product.offerQuantityFree,
                        offerTypeId: product.offerTypeID,
                        offerTypeName: product.offerTypeName
                      }}
                      handleQuantityChange={(
                        quantity: number,
                        catalogueRef: string
                      ) => {
                        if (!product || !product.catalogueRef) return;
                        addItemToBasket(0, catalogueRef, quantity);
                      }}
                      className="offer-prompt"
                      offerFreeProduct={offerFreeProduct}
                    />
                  </div>
                  {variants?.hasNoProductForCombination && (
                    <div className="d-none d-lg-block form-feedback form-feedback--errors text-center">
                      <p className="form-feedback__text">
                        No product is currently available for this combination
                      </p>
                    </div>
                  )}

                  <div className="d-none d-lg-flex flex-column">
                    {renderCtasAndQuantity()}
                    {product.directDelivery && (
                      <div className="product-details__delivery-notice">
                        <span>
                          <Icon
                            iconName="truck-new"
                            modifierCss="delivery-notice-icon"
                            size={2}
                          />
                        </span>
                        <span className="delivery-notice-text">
                          {product.directDeliveryText}
                        </span>
                      </div>
                    )}

                    <div className="product-details__ctas">
                      {!product.isRestricted && !product.isVariant && (
                        <>
                          {isCssFavouritesEnabled ? (
                            <div
                              className={`my-list ${
                                !isLoggedIn || false ? 'ml-0 w-25' : ''
                              }`}
                            >
                              <AddToMyList
                                catalogueRef={product.catalogueRef}
                                isLoggedIn={isLoggedIn}
                                quantity={
                                  typeof quantity === 'number' ? quantity : 1
                                }
                              />
                            </div>
                          ) : (
                            <>
                              <AddToFavourites
                                fallback={<Loader size="xsmall" />}
                                catalogueRef={product.catalogueRef}
                                isLoggedIn={isLoggedIn}
                                handleToggleFavourites={handleToggleFavourites}
                              />
                              {isLoggedIn ? (
                                <AddToWishlist
                                  catalogueRef={product.catalogueRef}
                                  size="sm"
                                  isBasketPage={false}
                                  handleAddedToWishlist={handleAddedToWishlist}
                                />
                              ) : (
                                <AddToSaveForLater
                                  quantity={quantity}
                                  catalogueRef={product.catalogueRef}
                                  modifier={product.modifier}
                                  size="sm"
                                />
                              )}
                            </>
                          )}

                          <AddToAny fallback={<Loader size="xsmall" />} />
                        </>
                      )}
                    </div>
                    {product.hasPriceBreak && renderPriceBreaks()}
                    {!product.isRestricted && !product.isVariant && (
                      <div className="product-details__ctas pt-lg-2">
                        {/* todo flag isIntl */}
                        <Download catalogueRef={product.catalogueRef} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-none d-lg-block mt-4 pt-5 bt-1 bc-grey-lighter">
        <div className="row">
          <div className="col-lg-6">{renderDescriptionTabsDesktop()}</div>
          <div className="col-lg-6 col-xl-5 offset-xl-1">
            {product.productLessons?.hasProductLessons && (
              <div
                className="product-details__product-lessons mb-4"
                id="productLessons"
              >
                <ProductLessons productLessons={product.productLessons} />
              </div>
            )}
            {showFeatures && (
              <section className="product-details__further-information mb-4">
                <h3 className="section-heading section-heading--sm pt-2">
                  Further Information
                </h3>
                <dl className="description-list container">
                  {product.features &&
                    product.features.length > 0 &&
                    product.features.map((feature: ProductFeature) => {
                      const featureValues: string[] | undefined =
                        feature.value?.split(',');
                      return (
                        <div
                          key={`${feature.name}`}
                          className="description-list__values row row-striped row-align-center p-1"
                        >
                          <dt className="description-title col-12 col-sm-4 col-md-6">
                            {feature.name}
                          </dt>
                          <dd className="description-detail col-12 col-sm-8 col-md-6 mb-0">
                            {featureValues &&
                              featureValues.map(
                                (value: string, index: number) => (
                                  <div key={`${index}`}>
                                    {value}
                                    {featureValues?.length !== index + 1 && (
                                      <br />
                                    )}
                                  </div>
                                )
                              )}
                          </dd>
                        </div>
                      );
                    })}
                </dl>
              </section>
            )}

            {showDocuments && (
              <section className="product-details__documents py-2 mb-4">
                <h3 className="section-heading section-heading--sm pt-2">
                  Documents
                </h3>
                <ul className="list-unstyled pl-3 mb-0">
                  {product?.documents?.map(
                    (document: ProductMediaItemViewModel) => (
                      <li key={document.productMediaID} className="py-1">
                        <a
                          href={`${document.value}`}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          {renderFileTypeIcon(document.mediaType)}{' '}
                          {document.title}
                        </a>
                      </li>
                    )
                  )}
                </ul>
              </section>
            )}

            {showUsefulLinks && (
              <section className="product-details__useful-links mb-4">
                <h3 className="section-heading section-heading--sm pt-2">
                  Useful Links
                </h3>
                <ul className="useful-links list-unstyled pl-3">
                  {product?.links?.map((link: ProductMediaItemViewModel) => (
                    <li key={link.productMediaID} className="py-1">
                      <a
                        className="d-block"
                        href={`${link.value}`}
                        target="_blank"
                        rel="noreferrer noopener"
                      >
                        {link.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="product-details__side">
              {transactionalCurrency !== null && (
                <div className="product-details__alternative">
                  <AlternativeProductPod
                    fallback={<Loader size="xsmall" />}
                    alternativeProducts={product.alternativeProducts}
                    transactionalCurrency={transactionalCurrency}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {false && (
        <JsonView
          data={{
            // location: props.location,
            // href: props.location && props.location.href,
            // path: props.path,
            // catalogueRefParam: catalogueRefParam,
            product
          }}
        />
      )}
    </main>
  );
};

export default Product;
